import type { ReactNode, ComponentProps } from "react";
import M360MarkdownLink from "@/lib/M360MarkdownLink";
import Icon from "@/components/Icon";
import Avatar from "@/components/Avatar/Avatar";
import { normalizeM360ImageSrc } from "@/lib/m360Paths";
import { normalizeChatDisplayText } from "../../normalizeChatDisplayText";
import { resolveFacilityLocationDisplay, sanitizeCardMeta, type RecordListItemFields } from "../cardDisplayUtils";
import { CardFacilityLocationLine } from "./CardFacilityLocationLine";
import { RecordPreviewRow } from "./RecordPreviewRow";

export type LinkItem = {
  label: string;
  href: string;
  description?: string | null;
};

export type ListItem = RecordListItemFields;

const navRowClassName =
  "flex w-full items-center gap-3 rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest p-3 text-left text-sys-brand-on-surface shadow-web-1 no-underline transition-colors hover:border-sys-brand-primary/40 hover:bg-sys-brand-surface-container-low focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary";

export function AgentNavRow({ href, children }: { href?: string | null; children: ReactNode }) {
  if (href) {
    return (
      <M360MarkdownLink href={href} className={navRowClassName}>
        {children}
      </M360MarkdownLink>
    );
  }

  return <div className={navRowClassName}>{children}</div>;
}

export function AgentLinkChip({ label, href, description }: LinkItem) {
  const displayLabel = normalizeChatDisplayText(label);

  return (
    <M360MarkdownLink
      href={href}
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest px-3 py-1.5 text-label-medium text-sys-brand-on-surface no-underline transition-colors hover:border-sys-brand-primary/40 hover:bg-sys-brand-surface-container-low"
      title={description ?? undefined}
    >
      <span aria-hidden className="shrink-0 text-sys-brand-primary">
        <Icon name="arrow_forward" size={16} />
      </span>
      <span className="truncate">{displayLabel}</span>
    </M360MarkdownLink>
  );
}

type AgentCardShellProps = {
  eyebrow: string;
  title: string;
  href?: string | null;
  subtitle?: string | null;
  meta?: string | null;
  facilityName?: string | null;
  facilityHref?: string | null;
  location?: string | null;
  kind?: ListItem["kind"];
  icon?: ComponentProps<typeof Icon>["name"];
  imageUrl?: string | null;
  links?: LinkItem[] | null;
  children?: ReactNode;
};

const cardHeaderClassName =
  "flex items-start gap-4 bg-gradient-to-br from-sys-brand-primary-container/25 via-sys-brand-surface-container-low to-sys-brand-surface-container-lowest p-4";

export function AgentCardShell({
  eyebrow,
  title,
  href,
  subtitle,
  meta,
  facilityName,
  facilityHref,
  location,
  kind,
  icon = "badge",
  imageUrl,
  links,
  children
}: AgentCardShellProps) {
  const displayTitle = normalizeChatDisplayText(title);
  const displayMeta = meta ? normalizeChatDisplayText(sanitizeCardMeta(meta) ?? "") : undefined;
  const safeImageUrl = normalizeM360ImageSrc(imageUrl);
  const facility = resolveFacilityLocationDisplay({
    title,
    subtitle,
    href,
    kind,
    facilityName,
    facilityHref,
    location,
    links
  });
  const splitLinks = Boolean(href && facility.facilityHref);

  const headerContent = (
    <>
      {safeImageUrl ? (
        <Avatar
          picture={safeImageUrl}
          size="large"
          className="shrink-0 ring-2 ring-sys-brand-surface-container-lowest"
        />
      ) : (
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sys-brand-secondary-container text-sys-brand-on-secondary-container"
          aria-hidden
        >
          <Icon name={icon} size={24} />
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-label-medium font-medium text-sys-brand-primary">{eyebrow}</p>
        {splitLinks ? (
          <h3 className="text-title-medium text-sys-brand-on-surface">
            <M360MarkdownLink href={href!} className="no-underline hover:text-sys-brand-primary">
              {displayTitle}
            </M360MarkdownLink>
          </h3>
        ) : (
          <h3 className="text-title-medium text-sys-brand-on-surface">{displayTitle}</h3>
        )}
        <CardFacilityLocationLine {...facility} />
        {displayMeta ? <p className="text-body-small text-sys-brand-on-surface">{displayMeta}</p> : null}
        {children}
      </div>
      {href && !splitLinks ? (
        <Icon
          name="chevron_right"
          size={20}
          className="shrink-0 self-center text-sys-brand-on-surface-variant"
          aria-hidden
        />
      ) : null}
    </>
  );

  return (
    <article className="overflow-hidden rounded-2xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest shadow-web-2">
      {href && !splitLinks ? (
        <M360MarkdownLink
          href={href}
          className={`${cardHeaderClassName} text-sys-brand-on-surface no-underline transition-colors hover:bg-sys-brand-primary-container/35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary`}
        >
          {headerContent}
        </M360MarkdownLink>
      ) : (
        <div className={cardHeaderClassName}>{headerContent}</div>
      )}
      {links?.filter((link) => !facility.facilityHref || link.href !== facility.facilityHref).length ? (
        <div className="flex flex-wrap gap-2 border-t border-sys-brand-outline-variant/70 bg-sys-brand-surface-container-lowest px-4 py-3">
          {links
            ?.filter((link) => !facility.facilityHref || link.href !== facility.facilityHref)
            .map((link) => (
              <AgentLinkChip key={`${link.href}-${link.label}`} {...link} />
            ))}
        </div>
      ) : null}
    </article>
  );
}

type AgentListProps = {
  title?: string | null;
  items: ListItem[];
  emptyLabel?: string;
};

export function AgentList({ title, items, emptyLabel = "No items" }: AgentListProps) {
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <section className="space-y-2">
      {title ? <h3 className="text-label-large text-sys-brand-on-surface">{normalizeChatDisplayText(title)}</h3> : null}
      {safeItems.length === 0 ? (
        <p className="text-body-small text-sys-brand-on-surface-variant">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2">
          {safeItems.map((item, index) => (
            <RecordPreviewRow key={`${item.title}-${index}`} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}
